import {
  addDoc,
  collection,
  deleteField,
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  KNOWLEDGE_ITEMS_COLLECTION,
  buildKnowledgeItemPayload,
} from './knowledgeItemModel';
import { buildKnowledgeEvidenceDocumentId } from './knowledgeEvidenceIdentity';
import { isCaregiverAuthoredProfileFact } from './caregiverProfileFact';

export const saveKnowledgeItem = async (item = {}, deps = {}) => {
  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const add = deps.addDoc || addDoc;
  const docFn = deps.doc || doc;
  const setDocument = deps.setDoc || setDoc;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const now = timestamp();
  const payload = buildKnowledgeItemPayload({
    ...item,
    capturedAt: item.capturedAt || now,
    createdAt: item.createdAt || now,
    updatedAt: item.updatedAt || now,
  });
  const evidenceDocumentId = buildKnowledgeEvidenceDocumentId({
    childId: item.childId,
    knowledgeType: payload.knowledgeType,
    profileField: payload.profileField,
    ...item.evidenceIdentity,
  });

  if (evidenceDocumentId) {
    // Use the deterministic evidence ID directly without a pre-read. Reading a
    // non-existent Firestore document fails the security rule (resource is null),
    // causing permission-denied before any write attempt. setDoc on an existing
    // document is an allowed update; on a new document it is an allowed create.
    const docRef = docFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION, evidenceDocumentId);
    await setDocument(docRef, payload);
    return {
      id: docRef.id,
      ...payload,
    };
  }

  const docRef = await add(collectionFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION), payload);

  return {
    id: docRef.id,
    ...payload,
  };
};

export const reinforceKnowledgeItem = async (
  existingItem = {},
  evidence = {},
  deps = {}
) => {
  const itemId = String(existingItem.id || '').trim();
  if (!itemId) throw new Error('Missing existing Care Knowledge item.');

  const firestoreDb = deps.db || db;
  const updateDocument = deps.updateDoc || updateDoc;
  const docFn = deps.doc || doc;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const now = timestamp();
  const sourceRefs = [
    ...(Array.isArray(existingItem.sourceRefs) ? existingItem.sourceRefs : []),
    evidence,
  ].filter((sourceRef, index, items) => {
    const identity = [
      sourceRef?.sourceCollection,
      sourceRef?.sourceId,
      sourceRef?.timelineEntryId,
    ].filter(Boolean).join(':');
    return identity && items.findIndex((candidate) => (
      [
        candidate?.sourceCollection,
        candidate?.sourceId,
        candidate?.timelineEntryId,
      ].filter(Boolean).join(':') === identity
    )) === index;
  }).slice(-10);

  await updateDocument(docFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION, itemId), {
    sourceRefs,
    lastObservedAt: now,
    updatedAt: now,
  });

  return { ...existingItem, sourceRefs, lastObservedAt: now, updatedAt: now };
};

// Updates the caregiver-editable text on an existing Knowledge item. Used by
// the Handbook's source-routed Edit sheet (DM_02 pilot) - never writes to a
// ChapterHandbookEntry, only to the underlying Knowledge doc, matching the
// "source is always the source" rule.
export const updateKnowledgeItemSummary = async (itemId = '', summary = '', deps = {}) => {
  const cleanItemId = String(itemId || '').trim();
  if (!cleanItemId) {
    throw new Error('Missing care knowledge item.');
  }

  const cleanSummary = String(summary || '').replace(/\s+/g, ' ').trim();
  if (!cleanSummary) {
    throw new Error('This information cannot be saved empty.');
  }

  const firestoreDb = deps.db || db;
  const updateDocument = deps.updateDoc || updateDoc;
  const docFn = deps.doc || doc;
  const timestamp = deps.serverTimestamp || serverTimestamp;

  await updateDocument(docFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION, cleanItemId), {
    summary: cleanSummary,
    editedByUser: true,
    updatedAt: timestamp(),
  });
};

export const updateCaregiverAuthoredProfileFact = async (fact = {}, summary = '', deps = {}) => {
  if (!isCaregiverAuthoredProfileFact(fact)) {
    throw new Error('Only information added directly through Profile can be edited here.');
  }
  return updateKnowledgeItemSummary(fact.id, summary, deps);
};

export const deleteCaregiverAuthoredProfileFact = async (fact = {}, deps = {}) => {
  if (!isCaregiverAuthoredProfileFact(fact)) {
    throw new Error('Only information added directly through Profile can be deleted here.');
  }

  const firestoreDb = deps.db || db;
  const deleteDocument = deps.deleteDoc || deleteDoc;
  const docFn = deps.doc || doc;
  await deleteDocument(docFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION, fact.id));
};

export const setKnowledgeItemProfileVisibility = async (itemId = '', hidden = false, deps = {}) => {
  const cleanItemId = String(itemId || '').trim();
  if (!cleanItemId) throw new Error('Missing care knowledge item.');

  const firestoreDb = deps.db || db;
  const updateDocument = deps.updateDoc || updateDoc;
  const docFn = deps.doc || doc;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const removeField = deps.deleteField || deleteField;

  await updateDocument(docFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION, cleanItemId), {
    profileVisibilityOverride: hidden ? 'hide' : removeField(),
    updatedAt: timestamp(),
  });
};

export const setKnowledgeItemFactStatus = async (itemId = '', factStatus = 'current', deps = {}) => {
  const cleanItemId = String(itemId || '').trim();
  if (!cleanItemId) throw new Error('Missing care knowledge item.');
  if (!['current', 'outdated'].includes(factStatus)) {
    throw new Error('Unsupported care knowledge status.');
  }

  const firestoreDb = deps.db || db;
  const updateDocument = deps.updateDoc || updateDoc;
  const docFn = deps.doc || doc;
  const timestamp = deps.serverTimestamp || serverTimestamp;

  await updateDocument(docFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION, cleanItemId), {
    factStatus,
    updatedAt: timestamp(),
  });
};

export const saveKnowledgeItemWithSupersession = async (item = {}, supersededItemId = '', deps = {}) => {
  const cleanSupersededItemId = String(supersededItemId || '').trim();
  if (!cleanSupersededItemId) throw new Error('Missing older care knowledge item.');

  const firestoreDb = deps.db || db;
  const collectionFn = deps.collection || collection;
  const docFn = deps.doc || doc;
  const batchFactory = deps.writeBatch || writeBatch;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const now = timestamp();
  const payload = buildKnowledgeItemPayload({
    ...item,
    capturedAt: item.capturedAt || now,
    createdAt: item.createdAt || now,
    updatedAt: item.updatedAt || now,
  });
  const evidenceDocumentId = buildKnowledgeEvidenceDocumentId({
    childId: item.childId,
    knowledgeType: payload.knowledgeType,
    profileField: payload.profileField,
    ...item.evidenceIdentity,
  });
  const itemsCollection = collectionFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION);
  const newItemRef = evidenceDocumentId
    ? docFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION, evidenceDocumentId)
    : docFn(itemsCollection);
  const oldItemRef = docFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION, cleanSupersededItemId);
  const batch = batchFactory(firestoreDb);

  batch.set(newItemRef, payload);
  batch.update(oldItemRef, { factStatus: 'outdated', updatedAt: now });
  await batch.commit();

  return { id: newItemRef.id, ...payload };
};

export const archiveKnowledgeItem = async (itemId = '', deps = {}) => {
  const cleanItemId = String(itemId || '').trim();
  if (!cleanItemId) {
    throw new Error('Missing care knowledge item.');
  }

  const firestoreDb = deps.db || db;
  const updateDocument = deps.updateDoc || updateDoc;
  const docFn = deps.doc || doc;
  const timestamp = deps.serverTimestamp || serverTimestamp;

  await updateDocument(docFn(firestoreDb, KNOWLEDGE_ITEMS_COLLECTION, cleanItemId), {
    status: 'archived',
    archivedAt: timestamp(),
    updatedAt: timestamp(),
  });
};
